import {
  formatUnitToFitDisplay,
  magnitudeUnitOf,
  optimalUnit,
  unitValueFormatter,
} from '@heviton/formatter';
import type { FormattedValue, ValueAxisResolution } from '@heviton/viz';
import { UI_TEXT } from '@/shared/languages';

// 차트/표 값 표현 공용 SSOT — 계측·MPPT·스트링 세 빌더가 공유(admin build-history-chart-model 미러). 색·단위·셀 포맷.

/**
 * 식별색 팔레트(cat) — **계열 구분 전용**(값 의미 없음). 인버터·MPPT·스트링 채널을 순번으로 칠한다(12색 초과 시
 * 순환). 에너지 의미색과 분리(tokens.css `--chart-cat-*`). 계측·MPPT·스트링 공용.
 */
export const CHART_CAT_PALETTE = Array.from({ length: 12 }, (_, i) => `var(--chart-cat-${i + 1})`);

/**
 * 라인 계열 정적 dot 정책(web 전역) — **끈다**(hover activeDot 만). viz 기본값은 켬이라 계열마다 명시해야 하는데,
 * 화면별로 판단하면 같은 앱 안에서 어떤 차트는 점이 있고 어떤 차트는 없는 상태가 된다(실제로 그랬다).
 *
 * 끄는 쪽으로 통일한 이유: web 차트는 슬롯이 16~31개(시간대·일·월)이고 계열도 여럿(발전소·인버터·채널)이라
 * 점을 찍으면 선이 점에 묻혀 추이를 못 읽는다. 값 확인은 hover 로 충분하다.
 *
 * **새 라인 계열은 이 상수를 쓴다** — 개별 판단 금지. 정책을 바꿀 일이 생기면 여기 한 곳만 뒤집는다.
 */
export const LINE_SHOW_DOTS = false;

/** 맨숫자(콤마·0~2 소수 trim) — magnitude 밖 단위의 축·툴팁 값 표기(단위는 정적 심볼로 분리). */
const plainNumber = (value: number): string =>
  formatUnitToFitDisplay(value, { minDecimals: 0, maxDecimals: 2 });

/**
 * PV/계측 표 셀 값 — 맨숫자(콤마·0~2 소수, 단위는 헤더에). null/undefined→`COMMON_EMPTY_VALUE`. MPPT·스트링 표
 * (`pvCell`)·계측 표(`measureCell` 수치 분기) 공용. 단위 변환·소수 하드코딩 금지(패키지 formatter SSOT).
 */
export const pvCell = (value: number | null | undefined): string =>
  value == null
    ? UI_TEXT.COMMON_EMPTY_VALUE
    : formatUnitToFitDisplay(value, { minDecimals: 0, maxDecimals: 2 });

/**
 * 차트 단위별 축·툴팁 정책(계측·MPPT·스트링 공용 SSOT) — admin `build-history-chart-model` 의 `unitHandlingOf`
 * 미러. `magnitudeUnitOf` 로 BE 단위 심볼(Wh·W·kW…)이 magnitude family 인지 판정 — magnitude 면 값은 **원단위
 * 그대로** 두고 `optimalUnit`(축 단일단위·from-aware·toggle 반응)·`formatUnitToFitDisplay`(툴팁 값별 자동단위)로
 * 표시만 변환한다(÷ 배율 없음). family 밖(V·A·Hz·%·분·무단위)은 raw + 정적 심볼(축 틱 소수 2자리로 0.1 이 0 으로
 * 뭉개지는 것 방지).
 */
export function unitHandlingOf(unit: string): {
  format: (value: number) => FormattedValue;
  resolve: (rawMax: number) => ValueAxisResolution;
} {
  const preset = magnitudeUnitOf(unit);
  if (preset) {
    return {
      format: (value) => ({ value: formatUnitToFitDisplay(value, preset), unit: '' }),
      resolve: (rawMax) => {
        const u = optimalUnit([rawMax], preset, 'max');
        return { tickFormatter: unitValueFormatter(preset, u), unit: u };
      },
    };
  }
  return {
    format: (value) => ({ value: plainNumber(value), unit }),
    resolve: () => ({ tickFormatter: plainNumber, unit }),
  };
}
